import { CopilotChart } from 'apps/sigma2-ui/src/components/CopilotChart';
import { HiringCopilotCustomVizDefinition } from 'libs/end-to-end/src/custom-viz/HiringCopilotDefinition';
import { HiringCopilotRequest } from 'libs/end-to-end/src/custom-viz/HiringCopilotTypes';
import { CopilotSlateView } from 'apps/sigma2-ui/src/components/CopilotSlateView';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useHiringCopilotUpdateDropdownsEffect } from 'apps/sigma2-ui/src/copilot/hooks/useHiringCopilotUpdateDropdownsEffect';
import { useHiringCopilotCurrentCustomVizRequestToExecute } from 'apps/sigma2-ui/src/copilot/hooks/useHiringCopilotCurrentCustomVizRequestToExecute';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { lastMonthDateRange } from 'libs/end-to-end/src/date-range';
import { useBasicCopilotAddToSlate } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotAddToSlate';
import { useBasicCopilotRemoveFromSlate } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotRemoveFromSlate';
import { useBasicCopilotCurrentState } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotCurrentState';
import { useBasicCopilotSubjectsWithSlateStatus } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotSubjectsWithSlateStatus';
import { useBasicCopilotSlateCopilotDataPoints } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotSlateCopilotDataPoints';
import { useBasicCopilotDepartmentsAndLocations } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotDepartmentsAndLocations';
import { useBasicCopilotDeptToRolesMap } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotDeptToRolesMap';
import { useHiringCopilotCanAnalyze } from 'apps/sigma2-ui/src/copilot/hooks/useHiringCopilotCanAnalyze';
import { useBasicCopilotData } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotData';
import { useBasicCopilotSubjectIdsToPrefill } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotSubjectIdsToPrefill';
import { useBasicCopilotPrefillSubjectIdsEffect } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotPrefillSubjectIdsEffect';
import { useBasicCopilotApplicableRoles } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotApplicableRoles';
import { useBasicCopilotOnFetchShareLink } from 'apps/sigma2-ui/src/copilot/hooks/useBasicCopilotOnFetchShareLink';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { useHiringCopilotLastPersistedRequest } from 'apps/sigma2-ui/src/copilot/hooks/useHiringCopilotLastPersistedRequest';
import { HiringAndPromotionCopilotWrapper } from 'apps/sigma2-ui/src/copilot/components/HiringAndPromotionCopilotWrapper';
import { getYYYYMMDDAsDate } from 'libs/end-to-end/src/dates';
import { useCopilotCustomVizAutoloader } from 'apps/sigma2-ui/src/lib/hooks/useCopilotCustomVizAutoloader';
import { MyObservation } from 'libs/end-to-end/src/my-observation';

export const HiringCopilotView = ({
  organizationId,
  myObservation,
}: {
  organizationId: string;
  myObservation: MyObservation;
}) => {
  const { userAuthenticatedApiCall } = useUser();

  const { getLevelByColumnValue } = useDimensionConfig();

  const context = useCustomViz();

  const {
    result,
    status,
    dropdownOptions,
    doViz,
    lastSuccessfulCustomVizRequest,
  } = useMemo(() => {
    return context;
  }, [context]);

  const [selectedSlateIds, setSelectedSlateIds] = useState<string[]>([]);

  const [currentShareLink, setCurrentShareLink] = useState<string | null>(null);

  const [showShareLinkModal, setShowShareLinkModal] = useState(false);

  const [showGraphTooltip, setShowGraphTooltip] = useState(false);

  const [nextSubjectIdToScrollTo, setNextSubjectIdToScrollTo] = useState<
    string | null
  >(null);

  const [currentlySelectedSubjectId, setCurrentlySelectedSubjectId] = useState<
    string | null
  >(null);

  // Using ref here since we don't want to recompute the last month date range on every render and moving outside of component would get date during build
  const lastMonthDateRangeRef = useRef(lastMonthDateRange());
  const [selectedStartDate, setSelectedStartDate] = useState<Date | undefined>(
    lastMonthDateRangeRef.current.start,
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>(
    lastMonthDateRangeRef.current.end,
  );

  const [shouldPrefillFilters, setShouldPrefillFilters] = useState(false);

  const [selectedDepartments, internalSetSelectedDepartments] = useState<
    string[]
  >([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  const [isFetchingShareLink, setIsFetchingShareLink] = useState(false);

  const addToSlate = useBasicCopilotAddToSlate({
    setSelectedSlateIds,
  });

  const removeFromSlate = useBasicCopilotRemoveFromSlate({
    setSelectedSlateIds,
  });

  const currentState = useBasicCopilotCurrentState({
    status,
    result,
  });

  const subjectsWithSlateStatus = useBasicCopilotSubjectsWithSlateStatus({
    selectedSlateIds,
    currentState,
    getLevelByColumnValue,
  });

  const slateCopilotDataPoints = useBasicCopilotSlateCopilotDataPoints({
    currentState,
    selectedSlateIds,
    getLevelByColumnValue,
  });

  const { departments, locations } = useBasicCopilotDepartmentsAndLocations({
    dropdownOptions,
  });

  const deptToRolesMap = useBasicCopilotDeptToRolesMap({
    dropdownOptions,
  });

  const { setSelectedDepartmentsWithRoles, currentApplicableRoles } =
    useBasicCopilotApplicableRoles({
      deptToRolesMap,
      setSelectedDepartments: internalSetSelectedDepartments,
      selectedDepartments,
      setSelectedRoles,
    });

  const canAnalyze = useHiringCopilotCanAnalyze({
    selectedEndDate,
    selectedLocations,
    status,
    selectedRoles,
    selectedDepartments,
    selectedStartDate,
  });

  const resetFiltersToInitialState = useHiringCopilotUpdateDropdownsEffect({
    setSelectedDepartments: internalSetSelectedDepartments,
    setSelectedRoles,
    dropdownOptions,
    setSelectedLocations,
    shouldPrefillFilters,
  });

  const currentCustomVizRequestToExecute =
    useHiringCopilotCurrentCustomVizRequestToExecute({
      selectedRoles,
      selectedStartDate,
      selectedEndDate,
      selectedDepartments,
      selectedLocations,
      observation: myObservation.type,
      organizationId,
      selectedSlateIds,
    });

  const { lastPersistedRequest } = useHiringCopilotLastPersistedRequest({
    myObservation,
    lastSuccessfulCustomVizRequest,
  });

  const copilotData = useBasicCopilotData({
    result,
  });

  const subjectIdsToPrefill = useBasicCopilotSubjectIdsToPrefill({
    copilotData,
  });

  useBasicCopilotPrefillSubjectIdsEffect({
    copilotData,
    setSelectedSlateIds,
    subjectIdsToPrefill,
  });

  const onFetchShareLink = useBasicCopilotOnFetchShareLink({
    request: currentCustomVizRequestToExecute,
    setShowShareLinkModal,
    setCurrentShareLink,
    userAuthenticatedApiCall,
    setIsFetchingShareLink,
    isFetchingShareLink,
  });

  const setSelections = useCallback((requestToLoad: HiringCopilotRequest) => {
    internalSetSelectedDepartments(requestToLoad.inputs.dropdowns.department);
    setSelectedRoles(requestToLoad.inputs.dropdowns.job_title);
    setSelectedLocations(requestToLoad.inputs.dropdowns.location);

    setSelectedStartDate(
      getYYYYMMDDAsDate(requestToLoad.inputs.dropdowns.start_date[0]),
    );
    setSelectedEndDate(
      getYYYYMMDDAsDate(requestToLoad.inputs.dropdowns.end_date[0]),
    );
    setSelectedSlateIds(requestToLoad.inputs.extras.your_list_subject_ids);
  }, []);

  const onAnalyse = useCallback(() => {
    if (!currentCustomVizRequestToExecute) {
      return;
    }

    doViz(currentCustomVizRequestToExecute);
  }, [currentCustomVizRequestToExecute, doViz]);

  const onAnalyseWithRequest = useCallback(
    (request: HiringCopilotRequest) => {
      doViz(request);
    },
    [doViz],
  );

  const { onUserAnalysisCancel } = useCopilotCustomVizAutoloader({
    canAnalyze,
    onAnalyze: onAnalyse,
    onAnalyseWithRequest,
    context,
    lastPersistedRequest,
    setSelections,
    setShouldPrefillFilters,
    shouldPrefillFilters,
    type: HiringCopilotRequest,
    resetDimensionsToInitialState: null,
    resetMetricsToInitialState: null,
    resetFiltersToInitialState,
  });

  return (
    <HiringAndPromotionCopilotWrapper
      chart={
        <CopilotChart
          subjectsWithSlateStatus={subjectsWithSlateStatus}
          addToSlate={addToSlate}
          applicableRoles={currentApplicableRoles}
          locations={locations}
          selectedDepartments={selectedDepartments}
          selectedRoles={selectedRoles}
          selectedLocations={selectedLocations}
          setSelectedDepartments={setSelectedDepartmentsWithRoles}
          setSelectedRoles={setSelectedRoles}
          setSelectedLocations={setSelectedLocations}
          selectedStartDate={selectedStartDate}
          selectedEndDate={selectedEndDate}
          setSelectedStartDate={setSelectedStartDate}
          setSelectedEndDate={setSelectedEndDate}
          canAnalyze={canAnalyze}
          currentCustomVizRequestToExecute={currentCustomVizRequestToExecute}
          currentlySelectedSubjectId={currentlySelectedSubjectId}
          currentShareLink={currentShareLink}
          departments={departments}
          removeFromSlate={removeFromSlate}
          setCurrentlySelectedSubjectId={setCurrentlySelectedSubjectId}
          setNextSubjectIdToScrollTo={setNextSubjectIdToScrollTo}
          setShowGraphTooltip={setShowGraphTooltip}
          setShowShareLinkModal={setShowShareLinkModal}
          showGraphTooltip={showGraphTooltip}
          showShareLinkModal={showShareLinkModal}
          slateCopilotDataPoints={slateCopilotDataPoints}
          focusArea={'Hiring'}
          onUserAnalysisCancel={onUserAnalysisCancel}
        />
      }
      slate={
        <CopilotSlateView
          focusArea={'Hiring'}
          definition={HiringCopilotCustomVizDefinition}
          setShowGraphTooltip={setShowGraphTooltip}
          setCurrentlySelectedSubjectId={setCurrentlySelectedSubjectId}
          removeFromSlate={removeFromSlate}
          currentlySelectedSubjectId={currentlySelectedSubjectId}
          addToSlate={addToSlate}
          subjectsWithSlateStatus={subjectsWithSlateStatus}
          setSelectedSlateIds={setSelectedSlateIds}
          onFetchShareLink={onFetchShareLink}
          isShareable={!!currentCustomVizRequestToExecute}
          isFetchingShareLink={isFetchingShareLink}
          nextSubjectIdToScrollTo={nextSubjectIdToScrollTo}
          selectedSlateIds={selectedSlateIds}
          subjectIdsToPrefill={subjectIdsToPrefill}
        />
      }
    />
  );
};
